// The single write behind the "Accepting scheduling" toggle.
//
// There is no accepting_scheduling column: a resource-day is closed exactly when an `omit: true` row
// covers it, the same predicate the plan page's shutoff gate (isResourceAcceptingBookings) reads.
//
// Reopening deletes THAT CLOSURE rather than flipping it to omit:false, because a surviving
// omit:false row is not inert — scheduleResource() keeps non-omit overrides, so it would shadow the
// rule underneath and the day would keep reading as a one-off.
//
// The exception is a day no rule covers: there the row is the resource's only presence on it, so it
// is flipped and kept. `coveredByRule` tells the two apart.
//
// Override rows that were already omit:false are never touched — a "Does not repeat" resource is
// stored as exactly one of those, and deleting it would erase the resource from the calendar.

export const NO_WRITE = { action: 'none' };

export const planSchedulingWrite = ({ resource, day, dayState, accepting }) => {
    const resourceId = resource?.resource_id;

    if (!resourceId || !day?.start || !day?.end || !dayState) {
        return NO_WRITE;
    }

    const { coveredByRule, blockingOverride, ruleEntity } = dayState;

    if (accepting) {
        if (!blockingOverride) {
            return NO_WRITE;
        }

        if (coveredByRule) {
            return { action: 'delete', mapping_id: blockingOverride.mapping_id };
        }

        // Bounds from the row, not the day — the column allows a range the UI never writes.
        return {
            action: 'set',
            override: {
                mapping_id: blockingOverride.mapping_id,
                resource_id: resourceId,
                override_start: blockingOverride.override_start,
                override_end: blockingOverride.override_end,
                omit: false,
            },
        };
    }

    if (blockingOverride) {
        return NO_WRITE;
    }

    // Closing a one-off's only day strands it: the card leaves the calendar and the toggle with it.
    // The dialog disables that direction, so this is the backstop rather than the guard.
    if (!coveredByRule) {
        return NO_WRITE;
    }

    return {
        action: 'set',
        override: {
            resource_id: resourceId,
            // The rule this closure excepts, from dayState's rules-only probe rather than from
            // `entity` — which names an override whenever one covers the day, so a day carrying an
            // extra-working-day row wrote rule_id: null. That matters: rule_id backs the
            // `rules { overrides }` relationship EditResourceModal clears when a rule is edited or
            // deleted, and the FK has no ON DELETE CASCADE, so an orphaned closure outlives its
            // rule and then reopens as a phantom one-off working day.
            rule_id: ruleEntity?.type === 'rules' ? ruleEntity.id : null,
            override_start: day.start,
            override_end: day.end,
            omit: true,
        },
    };
};

// The side dialog locks and unlocks a whole day at once, so its per-resource plans have to collapse
// into the two mutations that exist: one insert carrying the closures and the flips, one delete
// carrying the closures being dropped. NO_WRITE plans fall out here rather than at each call site.
export const collectSchedulingWrites = (plans = []) =>
    plans.filter(Boolean).reduce(
        (rv, plan) => {
            if (plan.action === 'set') {
                rv.overrides.push(plan.override);
            }

            if (plan.action === 'delete') {
                rv.deletes.push(plan.mapping_id);
            }

            return rv;
        },
        { overrides: [], deletes: [] }
    );
